// Dok 19 – Leveransintyg projektörsansvar.
// En sida som följer med handlingarna till ett projekt som inte använder systemet.
import type { Paragraph, Table } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body,
  buildDocument,
  factTable,
  fmtDate,
  h1,
  table,
} from "@/lib/documentGenerator/docTheme";
import { DESIGNER_CERTIFICATE_STATUS_LABELS } from "@/lib/basProcess/designerRoutine";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface DesignerCertificateCounts {
  identified: number;
  designedOut: number;
  residualConstruction: number;
  residualUse: number;
  deferred: number;
}

export interface DesignerCertificateContent {
  docNumber: string;
  title: string;
  count: number;
  note?: string;
}

export interface DesignerCertificateDoc {
  assignment: string | null;
  disciplines: string | null;
  phase_key: string | null;
  client_name: string | null;
  company: string | null;
  contact_name: string | null;
  contact_email: string | null;
  declaration: string | null;
  notes: string | null;
  status: string;
  version: string;
  signed_by: string | null;
  signed_at: string | null;
}

export const generateDesignerCertificateWordDocument = async (
  certificate: DesignerCertificateDoc,
  contents: DesignerCertificateContent[],
  counts: DesignerCertificateCounts,
  projectName?: string
) => {
  const documentName = "Leveransintyg projektörsansvar";
  const stamp = await createStampForCurrentUser({
    docNumber: "19",
    documentName,
    projectName,
  });

  const children: (Paragraph | Table)[] = [
    h1("1. Uppdraget"),
    factTable([
      ["Projekt", projectName ?? null],
      ["Uppdrag", certificate.assignment],
      ["Discipliner", certificate.disciplines],
      ["Skede", certificate.phase_key],
      ["Beställare/mottagare", certificate.client_name],
      ["Projektörsföretag", certificate.company],
      ["Kontaktperson", certificate.contact_name],
      ["E-post", certificate.contact_email],
      ["Leveransdatum", fmtDate(certificate.signed_at ?? new Date().toISOString())],
    ]),

    h1("2. Underlag som ingår i leveransen"),
    body(
      "Mottagaren behöver inget konto i systemet. Varje dokument är versionsmärkt och har en verifieringskod som kan kontrolleras.",
      { muted: true }
    ),
    table(
      ["Dok", "Dokument", "Antal", "Kommentar"],
      contents.map((c) => [`Dok ${c.docNumber}`, c.title, String(c.count), c.note ?? "–"]),
      { columnWidths: [1100, 4600, 1000, 2660] }
    ),

    h1("3. Sammanräkning av risker"),
    table(
      ["Post", "Antal"],
      [
        ["Identifierade arbetsmiljörisker", String(counts.identified)],
        ["Bortprojekterade (lösta i handling)", String(counts.designedOut)],
        ["Kvarvarande risker – byggskede", String(counts.residualConstruction)],
        ["Kvarvarande risker – bruksskede", String(counts.residualUse)],
        ["Framskjutna frågor med motiv", String(counts.deferred)],
      ],
      { columnWidths: [6660, 2700] }
    ),

    h1("4. Försäkran"),
    body(certificate.declaration ?? ""),

    h1("5. Så här arbetar vi"),
    body(
      "Arbetet bedrivs enligt Projektörens arbetsmiljörutin (Dok 18) som bifogas leveransen. Rutinen visar hur risker identifieras, byggs bort eller redovisas som kvarvarande, och hur frågor som inte kan avgöras i aktuellt skede skjuts fram med dokumenterat beslut."
    ),
  ];

  if (certificate.notes) {
    children.push(h1("6. Övriga upplysningar"), body(certificate.notes));
  }

  const footer = await documentFooter({
    docNumber: "19",
    documentType: "projektorintyg",
    signatureRoles: ["Projektör", "Uppdragsledare", "Mottagare (Bas-P/Byggherre)"],
  });

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "19",
      documentName,
      projectName,
      version: certificate.version || "1.0",
      status:
        DESIGNER_CERTIFICATE_STATUS_LABELS[certificate.status] || certificate.status,
      extra: [
        ["Undertecknad av", certificate.signed_by || "–"],
        ["Verifieringskod", stamp.code],
      ],
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(
    doc,
    `Dok19_Leveransintyg_${(projectName || "projekt").replace(/[^\w-]+/g, "_")}.docx`,
    {
      documentType: "projektorintyg",
      docNumber: "19",
      documentName,
      snapshot: { certificate, contents, counts },
      stamp,
    }
  );

  return stamp.code;
};

export default generateDesignerCertificateWordDocument;
